/**
 * Search DSL compiler family base + dialect contract.
 *
 * `SearchDslCompiler` owns the orchestration shared by Elasticsearch and
 * OpenSearch (how a plan becomes `_source`/`query`/`aggs`/`sort`/pagination).
 * Engine differences live in a `SearchDslDialect`; OpenSearch's dialect extends
 * Elasticsearch's and overrides only the fragments that differ.
 */
import type {
    QueryCompiler,
    SearchDslMutationResult,
    SearchDslTranspileResult,
} from '../compiler';
import { buildOutputMetadata } from '../../metadata/output-metadata-builder';
import type { MutationPlan } from '../../plan/mutation-plan';
import {
    AggregationNode,
    FieldRefNode,
    FilterConditionNode,
    FilterNode,
    GroupFieldNode,
    LogicalFilterNode,
    NotFilterNode,
    OrderNode,
    QueryPlan,
} from '../../plan/query-plan';

/** Engine-specific search DSL fragments. */
export interface SearchDslDialect {
    readonly engine: 'elasticsearch' | 'opensearch';

    renderTerm(field: FieldRefNode, value: unknown): Record<string, unknown>;

    renderRange(
        field: FieldRefNode,
        bounds: Partial<Record<'gt' | 'gte' | 'lt' | 'lte', unknown>>,
    ): Record<string, unknown>;

    renderWildcard(
        field: FieldRefNode,
        pattern: string,
    ): Record<string, unknown>;

    renderTermsAggregation(group: GroupFieldNode): Record<string, unknown>;

    renderDateHistogram(group: GroupFieldNode): Record<string, unknown>;

    renderSearchAfter(cursor: unknown[]): Record<string, unknown>;
}

/**
 * Shared search-DSL orchestration. The flow below is engine-agnostic; every
 * engine-specific fragment is delegated to `this.dialect`.
 *
 * NOTE: bucket ordering (`terms.order` from aggregation ordering) and per-level
 * bucket `size` (`GroupFieldNode.limit`) are left as TODOs in `buildAggs` — they
 * are refinements on top of the shared aggregation nesting.
 */
export abstract class SearchDslCompiler
    implements QueryCompiler<SearchDslTranspileResult>
{
    constructor(protected readonly dialect: SearchDslDialect) {}

    compile(plan: QueryPlan): SearchDslTranspileResult {
        const body: Record<string, unknown> = {};
        const grouped = plan.grouping !== null || plan.aggregations.length > 0;

        if (plan.projection) {
            body._source = plan.projection.fields.map((f) => esFieldName(f));
        }
        if (plan.filter) {
            body.query = this.buildQuery(plan.filter);
        }

        if (grouped) {
            body.aggs = this.buildAggs(plan);
            body.size = 0; // aggregation-only response
        } else {
            if (plan.ordering.length > 0) {
                body.sort = this.buildSort(plan.ordering);
            }
            body.size = plan.limits.limit;
            if (plan.pagination.cursor !== null) {
                Object.assign(
                    body,
                    this.dialect.renderSearchAfter(plan.pagination.cursor),
                );
            } else if (plan.pagination.skip !== null) {
                body.from = plan.pagination.skip;
            }
        }

        const paginationMode =
            plan.pagination.cursor !== null ? 'search_after' : 'offset';

        return {
            engine: this.dialect.engine,
            kind: 'search-dsl',
            index: plan.source.name,
            body,
            metadata: buildOutputMetadata(plan, paginationMode),
        };
    }

    compileMutation(plan: MutationPlan): SearchDslMutationResult {
        // insert → bulk index: reconstruct the documents from columns/rows so
        // the executor can build the bulk NDJSON for `plan.source.name`.
        if (plan.operation === 'insert') {
            const documents = plan.rows.map((row) => {
                const doc: Record<string, unknown> = {};
                plan.columns.forEach((col, i) => {
                    doc[col] = row[i];
                });
                return doc;
            });
            return {
                engine: this.dialect.engine,
                kind: 'search-dsl',
                operation: 'insert',
                index: plan.source.name,
                body: {},
                documents,
                ...(plan.conflictKeys ? { onConflict: plan.conflictKeys } : {}),
            };
        }

        // delete_by_query / update_by_query share the same body shape; the
        // `operation` tells the executor which endpoint to call.
        const body: Record<string, unknown> = {
            query: plan.filter
                ? this.buildQuery(plan.filter)
                : { match_all: {} },
        };

        if (plan.operation === 'update') {
            const params: Record<string, unknown> = {};
            const lines: string[] = [];
            plan.assignments.forEach((assignment, i) => {
                // Bracket access supports dotted field paths safely.
                const target = `ctx._source['${assignment.field.field}']`;
                if (assignment.op === 'removeField') {
                    lines.push(
                        `ctx._source.remove('${assignment.field.field}')`,
                    );
                    return;
                }
                const param = `p${i}`;
                params[param] = assignment.value;
                if (assignment.op === 'increment') {
                    lines.push(
                        `${target} = (${target} ?: 0) + params.${param}`,
                    );
                } else if (assignment.op === 'append') {
                    lines.push(
                        `if (${target} == null) { ${target} = [] } ${target}.add(params.${param})`,
                    );
                } else {
                    lines.push(`${target} = params.${param}`);
                }
            });
            body.script = {
                lang: 'painless',
                source: lines.join('; '),
                params,
            };
        }

        return {
            engine: this.dialect.engine,
            kind: 'search-dsl',
            operation: plan.operation,
            index: plan.source.name,
            body,
        };
    }

    private buildQuery(node: FilterNode): Record<string, unknown> {
        if (node instanceof FilterConditionNode) {
            return this.renderCondition(node);
        }
        if (node instanceof LogicalFilterNode) {
            const clauses = node.children.map((c) => this.buildQuery(c));
            return node.operator === 'and'
                ? { bool: { must: clauses } }
                : { bool: { should: clauses, minimum_should_match: 1 } };
        }
        if (node instanceof NotFilterNode) {
            return { bool: { must_not: [this.buildQuery(node.child)] } };
        }
        return {};
    }

    private renderCondition(
        node: FilterConditionNode,
    ): Record<string, unknown> {
        const { field } = node;
        switch (node.operator) {
            case 'eq':
                return this.dialect.renderTerm(field, node.value);
            case 'neq':
                return {
                    bool: {
                        must_not: [this.dialect.renderTerm(field, node.value)],
                    },
                };
            case 'gt':
            case 'gte':
            case 'lt':
            case 'lte':
                return this.dialect.renderRange(field, {
                    [node.operator]: node.value,
                });
            case 'in':
                return { terms: { [esFieldName(field)]: asArray(node.value) } };
            case 'notIn':
                return {
                    bool: {
                        must_not: [
                            {
                                terms: {
                                    [esFieldName(field)]: asArray(node.value),
                                },
                            },
                        ],
                    },
                };
            case 'contains':
                return this.dialect.renderWildcard(
                    field,
                    `*${escapeWildcard(String(node.value))}*`,
                );
            case 'notContains':
                return {
                    bool: {
                        must_not: [
                            this.dialect.renderWildcard(
                                field,
                                `*${escapeWildcard(String(node.value))}*`,
                            ),
                        ],
                    },
                };
            case 'startsWith':
                return this.dialect.renderWildcard(
                    field,
                    `${escapeWildcard(String(node.value))}*`,
                );
            case 'endsWith':
                return this.dialect.renderWildcard(
                    field,
                    `*${escapeWildcard(String(node.value))}`,
                );
            case 'between': {
                const [low, high] = asArray(node.value);
                return this.dialect.renderRange(field, {
                    gte: low,
                    lte: high,
                });
            }
            case 'exists':
            case 'isNotNull':
                return { exists: { field: esFieldName(field) } };
            case 'isNull':
                return {
                    bool: {
                        must_not: [{ exists: { field: esFieldName(field) } }],
                    },
                };
            default:
                return {};
        }
    }

    private buildAggs(plan: QueryPlan): Record<string, unknown> {
        const metrics: Record<string, unknown> = {};
        for (const agg of plan.aggregations) {
            metrics[agg.outputName] = this.metricAgg(agg);
        }

        const groups = plan.grouping?.groups ?? [];
        if (groups.length === 0) {
            return metrics;
        }

        // Nest from innermost group outward; innermost carries the metrics.
        let inner = metrics;
        for (let i = groups.length - 1; i >= 0; i -= 1) {
            const group = groups[i];
            const shell = group.granularity
                ? this.dialect.renderDateHistogram(group)
                : this.dialect.renderTermsAggregation(group);
            // TODO(bucket-order): merge bucket `order` (from aggregation ordering)
            // and per-level `size` (group.limit) into the terms/date_histogram shell.
            const node: Record<string, unknown> = { ...shell };
            if (Object.keys(inner).length > 0) {
                node.aggs = inner;
            }
            inner = { [group.outputName]: node };
        }
        return inner;
    }

    private metricAgg(agg: AggregationNode): Record<string, unknown> {
        if (agg.fn === 'count') {
            return {
                value_count: {
                    field: agg.field ? esFieldName(agg.field) : '_index',
                },
            };
        }
        return { [agg.fn]: { field: agg.field ? esFieldName(agg.field) : '' } };
    }

    private buildSort(ordering: OrderNode[]): unknown[] {
        const sort: unknown[] = [];
        for (const order of ordering) {
            if (order.target.kind === 'field') {
                sort.push({
                    [esFieldName(order.target.field)]: {
                        order: order.direction,
                    },
                });
            }
            // Aggregation ordering applies to buckets (see buildAggs TODO),
            // not to top-level sort in a row query.
        }
        return sort;
    }
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [value];
}

/**
 * Resolve the Elasticsearch/OpenSearch field name for a reference. A nested
 * path is expressed as a dotted name (`profile.address.city`), which is how
 * ES/OS address object sub-fields natively.
 */
export function esFieldName(node: FieldRefNode): string {
    return node.qualifiedName();
}

/**
 * Escape the wildcard metacharacters `*` and `?` (and the escape char `\`) so
 * user input in contains/startsWith/endsWith/notContains is matched literally
 * inside a `wildcard` query.
 */
export function escapeWildcard(value: string): string {
    return value
        .split('\\')
        .join('\\\\')
        .split('*')
        .join('\\*')
        .split('?')
        .join('\\?');
}
